import { View, Text, TouchableOpacity } from "react-native";
import { ReadOnlyProps } from "../../../../utils/templates";
import React, { useState, useEffect, useContext } from "react";
import * as Services from "../../../../utils/common";
import { StyleSheet } from "react-native";
import { DataTable, Button, Checkbox } from "react-native-paper";
import statusColorCodes from "../../../../utils/constants/statusColorCodes";
import Spinner from "../../../shared/Spinner";
import { formatValueAsDataType } from "../../../../utils/common";
import Icon from "react-native-vector-icons/FontAwesome5";
import ProductionTrackingForm from "../../../../screens/ChinaTracker/partials/ProductionTrackingForm";
import { ScrollView } from "react-native-gesture-handler";
import DeviceOrientationContext from "../../../context/DeviceOrientationContext";
import { useTranslation } from "../../../../utils/useTranslation";

interface Props {
  nestedHeader?: any;
  nestedRows?: Array<any>;
  isNestedDataLoaded?: boolean;
  rowKeyValue?: string;
  itemsPerPage: number;
  isDetail: boolean;
  isDisplayTimeWithDate?: boolean;
  setModalVisible: (event: any) => void;
  handleRowClick: (event: any) => void;
  setSelected: (event: any) => void;

  //exclude column to not have thousand separator format
  nestedColumnsToNotHaveThousandSeparator?: Array<string>;

  handleNestedSortColumn?: (
    columnKey: string,
    columnParentIdentifier: string,
  ) => void;
  disableHeader?: boolean;
  operationActions?: (order: any, action: string) => void;
  // New props for form functionality
  onFormSubmit?: (formData: any) => void;
  onFormUpdate?: (formData: any) => void;
  // Field types configuration
  fieldTypes?: Record<string, string>;
  currentRow: any;
  prefixRow: any;
}

export const ChinaNestedDataTableWithPagination: React.FC<
  ReadOnlyProps<Props>
> = ({
  nestedHeader,
  nestedRows,
  isNestedDataLoaded = true,
  rowKeyValue,
  itemsPerPage,
  isDisplayTimeWithDate = false,
  isDetail = false,
  setModalVisible,
  handleRowClick,
  setSelected,
  nestedColumnsToNotHaveThousandSeparator,
  handleNestedSortColumn,
  disableHeader,
  operationActions,
  onFormSubmit,
  onFormUpdate,
  currentRow,
  fieldTypes = {},
  prefixRow,
}: ReadOnlyProps<Props>) => {
    const [nestedPage, setNestedPage] = useState<number>(0);
    const [_nestedItemsPerPage] = useState<number>(itemsPerPage);
    const [formData, setFormData] = useState<any>({});
    const [isFormMode, setIsFormMode] = useState<boolean>(false);
    const [formModeActionNumber, setFormModeActionNumber] = useState<
      number | null
    >(null);
    const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

    // Default field types if not provided
    const defaultFieldTypes: Record<string, string> = {
      date_created: "date",
      date: "date",
      created_at: "date",
      updated_at: "date",
      createdAt: "date",
      ...fieldTypes,
    };

    useEffect(() => {
      // If no rows or empty array, set it to form mode
      setIsFormMode(!nestedRows || nestedRows.length === 0);

      // Initialize form data with empty values based on headers
      if (!nestedRows || nestedRows.length === 0) {
        const initialFormData: any = {};
        if (nestedHeader) {
          Object.keys(nestedHeader).forEach((key) => {
            if (defaultFieldTypes[key] === "date") {
              initialFormData[key] = new Date();
            } else {
              initialFormData[key] = "";
            }
          });
        }
        setFormData(initialFormData);
      }
    }, [nestedRows, nestedHeader]);

    const pagedRows = nestedRows?.slice(
      nestedPage * _nestedItemsPerPage,
      (nestedPage + 1) * _nestedItemsPerPage,
    );

    const handleRowClicking = (row: any, clickType: string) => {
      if (clickType === "details") {
        setModalVisible(true);
        setSelected(row);
      } else if (clickType === "edit") {
        // Set form data to the row data for editing
        const editData = { ...row };

        // Convert string dates to Date objects for the date picker
        Object.keys(editData).forEach((key) => {
          if (
            defaultFieldTypes[key] === "date" &&
            typeof editData[key] === "string"
          ) {
            editData[key] = new Date(editData[key]);
          }
        });

        setFormData(editData);
        setIsFormMode(true);
      }
    };

    const handleFormChange = (key: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const handleFormSubmit = () => {
      // Format dates back to strings if needed before submitting
      const submitData = { ...formData };

      if (editingRowIndex !== null && nestedRows && nestedRows.length > 0) {
        // Update existing row
        onFormUpdate?.(submitData);
      } else {
        // Create new row
        onFormSubmit?.(submitData);
      }
      setIsFormMode(false);
      setEditingRowIndex(null);

      // Reset form data
      const initialFormData: any = {};
      if (nestedHeader) {
        Object.keys(nestedHeader).forEach((key) => {
          if (defaultFieldTypes[key] === "date") {
            initialFormData[key] = new Date();
          } else {
            initialFormData[key] = "";
          }
        });
      }
      setFormData(initialFormData);
    };

    const handleCancelEdit = () => {
      setIsFormMode(false);
      setEditingRowIndex(null);

      // Reset form data
      const initialFormData: any = {};
      if (nestedHeader) {
        Object.keys(nestedHeader).forEach((key) => {
          if (defaultFieldTypes[key] === "date") {
            initialFormData[key] = new Date();
          } else {
            initialFormData[key] = "";
          }
        });
      }
      setFormData(initialFormData);
    };

    const onCancel = () => {
      setIsFormMode(false);
    };

    // Display form when in form mode or when no data is available
    if (isFormMode) {
      return (
        <ProductionTrackingForm
          onCancel={onCancel}
          editingRowIndex={editingRowIndex}
          initialData={pagedRows}
          isFormMode
          currentRow={currentRow}
          isEditMode={pagedRows?.length === 0 ? false : true}
          formModeActionNumber={formModeActionNumber}
          prefixRow={prefixRow}
        />
      );
    }

    const { isLargeDevice } = useContext(DeviceOrientationContext);
    const { t } = useTranslation();

    return (
      <ScrollView
        horizontal
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: "center", // vertical center
          alignItems: "center", // horizontal center
          paddingVertical: 8,
          paddingHorizontal: 12,
        }}
        // horizontal={isLargeDevice ? false : true}
        style={[styles.collapsableView, { padding: 0 }]}
      >
        <DataTable style={{ padding: 0 }}>
          {!disableHeader && (
            <DataTable.Header>
              {Object.keys(nestedHeader).map((key) => {
                return (
                  <DataTable.Title
                    style={{
                      flex: nestedHeader[key].width || 1,
                      justifyContent:
                        nestedHeader[key].justifyContent ?? "flex-start",
                    }}
                    key={`${Services.makeid(10)}_${key}`}
                  >
                    <View
                      style={{
                        width: "100%",
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 13,
                          width: "100%",
                          textAlign: nestedHeader[key].textAlign,
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {nestedHeader[key].name} &nbsp;&nbsp;
                        {nestedHeader[key].isSort &&
                          nestedRows &&
                          nestedRows.length > 1 && (
                            <TouchableOpacity
                              onPress={() =>
                                handleNestedSortColumn?.(
                                  key,
                                  nestedRows[0][rowKeyValue as string],
                                )
                              }
                            >
                              <Icon
                                name={
                                  nestedHeader[key].sortDirection === "asc"
                                    ? "sort-alpha-down"
                                    : "sort-alpha-up"
                                }
                                size={13}
                                color="black"
                              />
                            </TouchableOpacity>
                          )}
                      </Text>
                    </View>
                  </DataTable.Title>
                );
              })}
            </DataTable.Header>
          )}

          {!isNestedDataLoaded ? (
            (pagedRows as any[]).length > 0 ? (
              pagedRows?.map((_row, index) => {
                return (
                  <DataTable.Row
                    key={`${Services.makeid(10)}_${_row[rowKeyValue as string]}`}
                    style={{
                      borderColor: "#eee",
                      borderLeftWidth: 1,
                      borderRightWidth: 1,
                      borderBottomWidth: 1,
                    }}
                  >
                    {Object.keys(nestedHeader).map((_header) => {
                      const value = _row[_header];

                      const isCheckboxField = [
                        "labelOrder",
                        "directShipment",
                        "soakarPad",
                      ].includes(_header);
                      const isDateField = [
                        "planStartDay",
                        "actualStartDay",
                        "ESTCompletionDate",
                        "ESTPackDate",
                        "ETD",
                        "ETA",
                        "ExpectedArrival",
                        "actualCompletionDate",
                        "shippingDeadline",
                      ].includes(_header);

                      return (
                        <View
                          style={{
                            flex: nestedHeader[_header]?.width || 1,
                            borderColor: "white",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          key={Services.makeid(10)}
                        >
                          {["status", "ship_status", "orderStatus"].includes(
                            _header,
                          ) ? (
                            <TouchableOpacity
                              style={{
                                backgroundColor:
                                  statusColorCodes[value] || "#ccc",
                                paddingHorizontal: 12,
                                paddingVertical: 8,
                                borderRadius: 16,
                                minWidth: 100,
                                alignItems: "center",
                              }}
                              onPress={() => {
                                console.log(`${t("submit")}:`, _row.OrderNumber);
                              }}
                            >
                              <Text style={{ color: "white", fontWeight: "600" }}>
                                {t("submit")}
                              </Text>
                            </TouchableOpacity>
                          ) : isCheckboxField ? (
                            <View
                              style={{
                                width: "100%",
                              }}
                            >
                              <Checkbox
                                status={value ? "checked" : "unchecked"}
                                onPress={() => { }}
                              />
                            </View>
                          ) : isDateField ? (
                            <Text
                              selectable={true}
                              style={{
                                fontSize: 12,
                                width: "100%",
                                textAlign: nestedHeader[_header]?.textAlign || "left",
                                userSelect: "text",
                              } as any}
                            >
                              {Services.formatLocalDate(value)}
                            </Text>
                          ) : ["runOrder"].includes(_header) ? (
                            <Text
                              selectable={true}
                              style={{
                                fontSize: 12,
                                width: "100%",
                                textAlign: nestedHeader[_header]?.textAlign || "left",
                                userSelect: "text",
                              } as any}
                            >
                              {`${t("run")} ${value}`}
                            </Text>
                          ) : ["actions"].includes(_header) ? (
                            <View
                              style={{
                                flexDirection: "row", // Horizontal layout
                                justifyContent: "flex-start", // Align to left
                                alignItems: "center",
                                gap: 8,
                                paddingVertical: 8,
                              }}
                            >
                              <TouchableOpacity
                                style={[styles.floatingAddButton]}
                                onPress={() => {
                                  setIsFormMode(true);
                                  setFormModeActionNumber(index);
                                }}
                              >
                                <Text style={styles.buttonText2}>
                                  {pagedRows.length > 0
                                    ? t("resubmit")
                                    : t("submit")}
                                </Text>
                              </TouchableOpacity>

                              <TouchableOpacity
                                style={[
                                  styles.floatingAddButton,
                                  { backgroundColor: "#36a64f" },
                                ]}
                                onPress={() => {
                                  setIsFormMode(true);
                                  setFormModeActionNumber(null);
                                }}
                              >
                                <Text style={styles.buttonText2}>
                                  {pagedRows.length > 0 ? t("shipments") : ""}
                                </Text>
                              </TouchableOpacity>

                              <TouchableOpacity
                                style={[
                                  styles.floatingAddButton,
                                  { backgroundColor: "#081D2C" },
                                ]}
                                onPress={() => {
                                  setIsFormMode(true);
                                  setFormModeActionNumber(null);
                                }}
                              >
                                <Text style={styles.buttonText2}>
                                  {pagedRows.length > 0 ? t("add") : ""}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          ) : (
                            <Text
                              selectable={true}
                              style={{
                                fontSize: 12,
                                width: "100%",
                                textAlign: nestedHeader[_header]?.textAlign || "left",
                                userSelect: "text",
                              } as any}
                            >
                              {nestedColumnsToNotHaveThousandSeparator?.includes(
                                nestedHeader[_header].name,
                              ) || ["orderDate", "requestDate"].includes(_header)
                                ? value
                                : nestedHeader[_header].name.includes("Uom1")
                                  ? `${formatValueAsDataType(value)} (${_row["InvntryUom"]
                                  })`
                                  : nestedHeader[_header].name.includes("Uom2")
                                    ? `${formatValueAsDataType(value)} (${_row["Uom2"]
                                    })`
                                    : formatValueAsDataType(value)}
                            </Text>
                          )}
                        </View>
                      );
                    })}
                  </DataTable.Row>
                );
              })
            ) : (
              <View style={styles.noDataBanner}>
                <Button
                  style={styles.addNewButton}
                  onPress={() => setIsFormMode(true)}
                >
                  <Text style={styles.addButtonText}>
                    <Icon name="plus" size={15} color="white" /> {t("addNew")}
                  </Text>
                </Button>
              </View>
            )
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "5%" }}
              />
            </View>
          )}
        </DataTable>
      </ScrollView>
    );
  };

const styles = StyleSheet.create({
  collapsableView: {
    flex: 1,
    borderStyle: "solid",
    borderColor: "rgba(47, 149, 220, 0.5)",
    borderWidth: 1,
    position: "relative",
  },
  noDataBanner: {
    margin: "2%",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  orderStatusDate: {
    fontSize: 12,
    textAlign: "center",
    color: "#C0BCBC",
  },
  actionsIcons: {
    paddingHorizontal: 8,
  },
  actionsIconsMobile: {
    paddingHorizontal: 2,
  },
  menuOptionContainer: {
    flex: 1,
    marginHorizontal: 2,
  },
  formScrollContainer: {
    padding: 15,
  },
  formRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  formFieldHorizontal: {
    flex: 1,
    marginHorizontal: 5,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  formInput: {
    borderWidth: 1,
    borderColor: "rgba(47, 149, 220, 0.5)",
    borderRadius: 5,
    padding: 10,
    fontSize: 14,
  },
  datePickerInput: {
    backgroundColor: "transparent",
  },
  formButtonContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
  },
  formButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
    marginLeft: 10,
  },
  cancelButton: {
    backgroundColor: "#C0BCBC",
  },
  submitButton: {
    backgroundColor: "rgba(47, 149, 220, 1)",
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
  },
  buttonText2: {
    color: "white",
    fontWeight: 700,
    fontSize: 10,
  },
  addNewButton: {
    backgroundColor: "rgba(47, 149, 220, 1)",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  addButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 14,
  },
  floatingAddButton: {
    marginTop: 10,
    backgroundColor: "#56B7FD",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 5,
    minWidth: 100,
    height: 28,
  },
});
